import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Container, Grid, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useSearchParams } from 'react-router-dom';
import api, { SINGLE_SYMBOL } from '../../infrastructure/config/api';
import Button from '../components/common/Button';
import {
  LearningHeader,
  LearningRunDetailDrawer,
  LearningRunsTable,
  ProfileCard,
  ProfileDiffCard,
  TradeQualityCard,
} from '../components/learning';

const SingleSymbolLearning = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const [symbol, setSymbol] = useState(() => String(searchParams.get('symbol') || 'ETHUSDT').toUpperCase());
  const [profileLoading, setProfileLoading] = useState(false);
  const [runsLoading, setRunsLoading] = useState(false);
  const [qualityLoading, setQualityLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [runs, setRuns] = useState([]);
  const [tradeQuality, setTradeQuality] = useState(null);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [selectedRun, setSelectedRun] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  useEffect(() => {
    const querySymbol = String(searchParams.get('symbol') || 'ETHUSDT').toUpperCase();
    if (querySymbol !== symbol) {
      setSymbol(querySymbol);
    }
  }, [searchParams, symbol]);

  const fetchPageData = useCallback(async () => {
    setProfileLoading(true);
    setRunsLoading(true);
    setQualityLoading(true);
    try {
      const [profileRes, runsRes, qualityRes] = await Promise.all([
        api.get(SINGLE_SYMBOL.PROFILE, { params: { symbol } }),
        api.get(SINGLE_SYMBOL.LEARNING_RUNS, { params: { symbol } }),
        api.get(SINGLE_SYMBOL.TRADE_QUALITY, { params: { symbol } }),
      ]);

      setProfile(profileRes.data || null);
      setRuns(runsRes.data?.items || []);
      setTradeQuality(qualityRes.data || null);
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || 'Failed to load single-symbol learning data', {
        variant: 'error',
      });
    } finally {
      setProfileLoading(false);
      setRunsLoading(false);
      setQualityLoading(false);
    }
  }, [enqueueSnackbar, symbol]);

  const fetchRunDetail = useCallback(async (runId) => {
    setDetailLoading(true);
    setDetailError('');
    setSelectedRunId(runId);
    try {
      const response = await api.get(SINGLE_SYMBOL.LEARNING_RUN_DETAIL(runId), {
        params: { symbol },
      });
      setSelectedRun(response.data || null);
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || 'Failed to load learning run detail', {
        variant: 'error',
      });
      setSelectedRun(null);
      setDetailError(error.response?.data?.error || 'Failed to load learning run detail');
    } finally {
      setDetailLoading(false);
    }
  }, [enqueueSnackbar, symbol]);

  useEffect(() => {
    fetchPageData();
  }, [fetchPageData]);

  const diffMeta = useMemo(() => {
    if (selectedRun?.validation) {
      return {
        changedFields: selectedRun.validation.changed_fields || [],
        changedFieldsCount: selectedRun.validation.changed_fields_count || 0,
      };
    }

    const activeProfile = profile?.profile?.active_profile || {};
    const proposedProfile = profile?.profile?.proposed_profile || null;
    if (!proposedProfile) {
      return {
        changedFields: [],
        changedFieldsCount: 0,
      };
    }

    const fields = Object.keys(proposedProfile).filter((field) => (
      JSON.stringify(activeProfile?.[field] ?? null) !== JSON.stringify(proposedProfile?.[field] ?? null)
    )).sort();

    return {
      changedFields: fields,
      changedFieldsCount: fields.length,
    };
  }, [profile, selectedRun]);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Single Symbol Learning</Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          label="Symbol"
          size="small"
          value={symbol}
          onChange={(event) => setSymbol(String(event.target.value || '').toUpperCase())}
        />
        <Button
          variant="contained"
          onClick={() => {
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              next.set('symbol', symbol);
              return next;
            });
            fetchPageData();
          }}
          loading={profileLoading || runsLoading || qualityLoading}
        >
          Refresh
        </Button>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <LearningHeader
            symbol={profile?.symbol || symbol}
            scheduler={profile?.scheduler}
            learningThreshold={{
              requiredClosedTrades: profile?.learning_threshold?.required_closed_trades || 0,
              currentClosedTrades: profile?.learning_threshold?.current_closed_trades || 0,
              readyToLearn: profile?.learning_threshold?.ready_to_learn || false,
            }}
            profileVersion={profile?.profile?.profile_version || 1}
            lastEvaluatedAt={profile?.profile?.last_evaluated_at || null}
            lastActivatedAt={profile?.profile?.last_activated_at || null}
            lastRunStatus={profile?.proposal_meta?.last_run_status || null}
            lastRunReasonCode={profile?.proposal_meta?.reason_code || null}
            lastRunReasonMeta={profile?.proposal_meta?.reason_meta || {}}
            loading={profileLoading}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <ProfileCard title="Active Profile" profile={profile?.profile?.active_profile || null} loading={profileLoading} />
        </Grid>
        <Grid item xs={12} md={4}>
          <ProfileCard title="Proposed Profile" profile={profile?.profile?.proposed_profile || null} emptyMessage="Chưa có proposed profile." loading={profileLoading} />
        </Grid>
        <Grid item xs={12} md={4}>
          <TradeQualityCard
            closedTrades={tradeQuality?.closed_trades || 0}
            requiredClosedTrades={tradeQuality?.required_closed_trades || 0}
            enrichment={{
              complete: tradeQuality?.enrichment?.complete || 0,
              partial: tradeQuality?.enrichment?.partial || 0,
              failed: tradeQuality?.enrichment?.failed || 0,
              completeRatio: tradeQuality?.enrichment?.complete_ratio || 0,
            }}
            metrics={tradeQuality?.metrics || {}}
            warnings={tradeQuality?.warnings || []}
            loading={qualityLoading}
          />
        </Grid>

        <Grid item xs={12}>
          <ProfileDiffCard
            activeProfile={profile?.profile?.active_profile || null}
            proposedProfile={profile?.profile?.proposed_profile || null}
            changedFields={diffMeta.changedFields}
            changedFieldsCount={diffMeta.changedFieldsCount}
            loading={profileLoading}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 1 }}>Learning Runs</Typography>
          <LearningRunsTable items={runs} onSelectRun={fetchRunDetail} loading={runsLoading} />
        </Grid>
      </Grid>

      <LearningRunDetailDrawer
        open={Boolean(selectedRunId)}
        run={selectedRun}
        loading={detailLoading}
        error={detailError}
        onClose={() => {
          setSelectedRunId(null);
          setSelectedRun(null);
          setDetailError('');
        }}
      />
    </Container>
  );
};

export default SingleSymbolLearning;
